// ===== FILE: src/ContentBlocks/ContentBlock.js =====
// Original code preserved with an added 'worksheet' case that renders
// your WorksheetBlock.jsx. Import path matches your tree.

import React, { useEffect, useRef, useState } from 'react';
import SafeHtml from '../components/SafeHtml';
import {
    ChevronDown,
    AlertCircle,
    CheckCircle,
    AlertTriangle,
    Edit3,
    X,
    GripVertical,
    ChevronUp
} from 'lucide-react';
import AddBlockMenu from './Section';

import { RichTextEditor, HeadlineEditor } from '../components/LazyEditors';
import CodeBlock from '../components/CodeBlock/CodeBlock';
import EmbeddedHtmlRenderer from './renderers/EmbeddedHtmlRenderer';
import CustomHtmlRenderer from './renderers/CustomHtmlRenderer';
import ImageDrawoverRenderer from './renderers/ImageDrawoverRenderer';
import ImageEmbedFrame from './renderers/ImageEmbedFrame';
import { isEmbeddedImage } from '../utils/imageEmbed';
import { getDisplayCaption } from '../utils/imageCaption';

import {
    generateAudioCitation
} from '../utils/contentUtils.js';
import {
    buildImageSourceMeta,
    buildVideoSourceMeta,
    formatFigureNote,
    formatInText,
    formatTableNote
} from '../lib/apaSlideFormatter.js';

// NEW: render the worksheet block
import WorksheetBlock from './WorksheetBlock';
import AccordionRenderer from './renderers/AccordionRenderer';
import CardRenderer from './renderers/CardRenderer';
import MultiColumnCardRenderer from './renderers/MultiColumnCardRenderer';
import TimelineRenderer from './renderers/TimelineRenderer';
import LinkRenderer from './renderers/LinkRenderer';
import { DividerBlock } from '../ContentBlockSystem';
import ApaQuoteRenderer from './renderers/ApaQuoteRenderer';
import ApaCitationRenderer from './renderers/ApaCitationRenderer';
import ApaReferenceListRenderer from './renderers/ApaReferenceListRenderer';
import YouTubePlaylistRenderer from './renderers/YouTubePlaylistRenderer';

const getContrastingTextColor = (hexColor) => {
    if (!hexColor || typeof hexColor !== 'string') return '#0f172a';
    const sanitized = hexColor.replace('#', '');
    if (![3, 6].includes(sanitized.length)) return '#0f172a';
    const fullHex = sanitized.length === 3
        ? sanitized.split('').map((c) => c + c).join('')
        : sanitized;
    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#0f172a';
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 150 ? '#0f172a' : '#f8fafc';
};

const AudioPlayer = ({ src, description, citation }) => {
    return (
        <div className="my-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <audio controls className="w-full mb-3">
                <source src={src} type="audio/mpeg" />
                <source src={src} type="audio/wav" />
                <source src={src} type="audio/ogg" />
                Your browser does not support the audio element.
            </audio>
            {description && (
                <div className="text-sm text-gray-600 mb-2">{description}</div>
            )}
            {citation && (
                <SafeHtml className="bg-white border border-gray-200 p-3 rounded-lg text-sm text-gray-600"
                    html={citation} />
            )}
        </div>
    );
};

const ContentBlock = ({
    block,
    onEdit,
    onDelete,
    isEditMode,
    onDragStart,
    onDrop,
    onDragOver,
    onBlockUpdate,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
    htmlModes,
    toggleHtmlMode,
    onAddBlockBelow,
    sectionId,
    handleWorksheetJsonImport,
    handleWorksheetJsonPaste,
    onPresetImageRequest,
    onPresetVideoRequest,
    autoApaCitations
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [activeStackedCardIndex, setActiveStackedCardIndex] = useState(null);
    const blockRef = useRef(null);

    useEffect(() => {
        if (!isEditMode) {
            setActiveStackedCardIndex(null);
        }
    }, [isEditMode]);

    const handleDragStart = (e) => {
        if (e.target.closest('.drag-handle')) {
            setIsDragging(true);
            onDragStart && onDragStart(e, block.id);
        } else {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleDragEnd = () => setIsDragging(false);
    const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); onDrop && onDrop(e); };
    const handleDragOver = (e) => { e.preventDefault(); onDragOver && onDragOver(e); };
    const handleBlockMouseDown = (e) => { if (e.target.closest('.drag-handle')) e.stopPropagation(); };

    const handleAddContent = (contentType) => {
        if (onAddBlockBelow) {
            onAddBlockBelow(block.id, contentType, sectionId);
        } else {
            alert(`Add ${contentType} block functionality needs to be implemented in the parent component`);
        }
    };

    const renderContent = () => {
        switch (block.type) {
            case 'text':
            case 'heading':
            case 'list':
            case 'table':
                return isEditMode ? (
                    <div onClick={(e) => e.stopPropagation()}>
                        <RichTextEditor
                            content={block.content}
                            onChange={(content) => onBlockUpdate({ ...block, content })}
                            isHtmlMode={htmlModes[block.id] || false}
                            onToggleHtmlMode={() => toggleHtmlMode(block.id)}
                            isPreviewMode={false}
                        />
                    </div>
                ) : (
                    <SafeHtml className="rich-editor-content" html={block.content} />
                );

            case 'headline':
                return isEditMode ? (
                    <div onClick={(e) => e.stopPropagation()}>
                        <HeadlineEditor
                            content={block.content}
                            onChange={(content) => onBlockUpdate({ ...block, content })}
                        />
                    </div>
                ) : (
                    <SafeHtml className="headline-preview" html={block.content} />
                );

            case 'html':
                return (
                    <div onClick={(e) => e.stopPropagation()}>
                        <CodeBlock
                            id={block.id}
                            initial={{
                                content: block.content ?? '',
                                language: block.language ?? 'html',
                                mode: block.mode ?? 'displayCode',
                                customCss: block.customCss ?? '',
                                citation: block.citation ?? '',
                                options: {
                                    softWrap: block.options?.softWrap ?? false,
                                    showLineNumbers: block.options?.showLineNumbers ?? true,
                                    allowScripts: block.options?.allowScripts ?? false,
                                },
                            }}
                            readOnly={!isEditMode}
                            onChange={(s) =>
                                onBlockUpdate({
                                    ...block,
                                    content: s.content,
                                    language: s.language,
                                    mode: s.mode,
                                    options: s.options,
                                    customCss: s.customCss,
                                    citation: s.citation,
                                })
                            }
                        />
                    </div>
                );

            case 'info-box':
            case 'exercise-box':
            case 'warning-box': {
                const boxConfig = {
                    'info-box': { bg: 'bg-blue-50', border: 'border-l-4 border-blue-400', icon: <AlertCircle className="text-blue-500" size={20} /> },
                    'exercise-box': { bg: 'bg-emerald-50', border: 'border-l-4 border-emerald-400', icon: <CheckCircle className="text-emerald-500" size={20} /> },
                    'warning-box': { bg: 'bg-amber-50', border: 'border-l-4 border-amber-400', icon: <AlertTriangle className="text-amber-500" size={20} /> }
                }[block.type];
                return (
                    <div className={`p-4 rounded-lg ${boxConfig.bg} ${boxConfig.border}`}>
                        <div className="flex items-start gap-3">
                            {boxConfig.icon}
                            <div className="flex-1">
                                {isEditMode ? (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <RichTextEditor
                                            content={block.content}
                                            onChange={(content) => onBlockUpdate({ ...block, content })}
                                            isHtmlMode={htmlModes[block.id] || false}
                                            onToggleHtmlMode={() => toggleHtmlMode(block.id)}
                                            isPreviewMode={false}
                                        />
                                    </div>
                                ) : (
                                    <SafeHtml className="rich-editor-content" html={block.content} />
                                )}
                            </div>
                        </div>
                    </div>
                );
            }

            case 'video': {
                const aspectClass = { '16-9': 'pb-[56.25%]', '4-3': 'pb-[75%]', '1-1': 'pb-[100%]', '21-9': 'pb-[42.85%]' }[block.aspectRatio] || 'pb-[56.25%]';
                const includeVideoCitation = block.videoIncludeCitation !== false;
                const videoMeta = buildVideoSourceMeta(block);
                const videoCitation = formatInText(videoMeta);
                const videoUrl = (videoMeta.url || '').trim();
                const hasVideoCitation = includeVideoCitation && Boolean(videoCitation || videoUrl);
                return (
                    <div className="my-6">
                        <div className={`relative w-full ${aspectClass} overflow-hidden rounded-xl bg-gray-900 shadow-lg`}>
                            <iframe
                                src={block.src}
                                className="absolute top-0 left-0 w-full h-full border-0"
                                allowFullScreen
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                referrerPolicy="strict-origin-when-cross-origin"
                                title={`embed-${String((block && (block.title || block.id)) || 'frame')}`}
                             />
                        </div>
                        {hasVideoCitation && (
                            <div className="bg-gray-50 border border-gray-200 p-3 mt-3 rounded-lg text-sm text-gray-600">
                                {videoCitation}
                                {videoUrl && (
                                    <a
                                        href={videoUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                                    >
                                        {videoUrl}
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                );
            }

            case 'youtube-playlist': {
                return (
                    <YouTubePlaylistRenderer
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                    />
                );
            }

            case 'image': {
                const sizeClass = { small: 'max-w-xs', medium: 'max-w-md', large: 'max-w-2xl', full: 'w-full' }[block.size] || 'max-w-md';
                const imageCitation = (() => {
                    if (block.imageIncludeCitation === false) return '';
                    const sourceMeta = buildImageSourceMeta(block, {
                        sourceType: block.imageSourceType || 'image',
                        page: block.imagePage || '',
                        license: block.imageLicense || '',
                        isAdapted: Boolean(block.imageIsAdapted),
                        section: block.imageTableNumber || ''
                    });
                    return sourceMeta.sourceType === 'table'
                        ? formatTableNote(sourceMeta)
                        : formatFigureNote(sourceMeta);
                })();
                return (
                    <div className="my-6 text-center">
                        {isEmbeddedImage(block)
                            ? <ImageEmbedFrame block={block} />
                            : <img src={block.src} alt={block.alt} className={`${sizeClass} h-auto rounded-xl shadow-lg mx-auto`} />}
                        {getDisplayCaption(block) && (
                            <SafeHtml className="bg-gray-50 border border-gray-200 p-3 mt-3 rounded-lg text-sm text-gray-600 text-left max-w-2xl mx-auto"
                                html={getDisplayCaption(block)} />
                        )}
                        {imageCitation && (
                            <div className="bg-gray-50 border border-gray-200 p-3 mt-2 rounded-lg text-sm text-gray-600 text-left max-w-2xl mx-auto">
                                {imageCitation}
                            </div>
                        )}
                    </div>
                );
            }

            case 'gallery': {
                const cols = block.columns || '2';
                const gridClass = cols === '4' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4' :
                                  cols === '3' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' :
                                  'grid-cols-1 sm:grid-cols-2';
                return (
                    <div className="my-6">
                        <div className={`grid ${gridClass} gap-4 print-gallery-cols-${cols}`}>
                            {block.items && block.items.map((item, index) => {
                                const itemCitation = (() => {
                                    if (item.imageIncludeCitation === false) return '';
                                    const sourceMeta = buildImageSourceMeta(item, {
                                        sourceType: item.imageSourceType || 'image',
                                        page: item.imagePage || '',
                                        license: item.imageLicense || '',
                                        isAdapted: Boolean(item.imageIsAdapted),
                                        section: item.imageTableNumber || ''
                                    });
                                    return sourceMeta.sourceType === 'table'
                                        ? formatTableNote(sourceMeta)
                                        : formatFigureNote(sourceMeta);
                                })();
                                return (
                                    <div key={index} className="group flex flex-col">
                                        <div className="flex justify-center items-center bg-gray-100 rounded-lg overflow-hidden">
                                            <img src={item.src} alt={item.alt} className="max-w-full h-auto object-contain self-center rounded-lg shadow-md group-hover:shadow-lg transition-shadow" />
                                        </div>
                                        {getDisplayCaption(item) && (
                                            <SafeHtml className="bg-gray-50 border border-gray-200 p-2 mt-2 rounded-lg text-sm text-gray-600"
                                                html={getDisplayCaption(item)} />
                                        )}
                                        {itemCitation && (
                                            <div className="bg-gray-50 border border-gray-200 p-2 mt-1 rounded-lg text-sm text-gray-600">
                                                {itemCitation}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            case 'audio': {
                const audioCitation = generateAudioCitation(block.audioTitle, block.audioCreator, block.audioSourceInfo, block.audioDateInfo);
                return <AudioPlayer src={block.src} description={block.description} citation={audioCitation} />;
            }

            case 'link': {
                return <LinkRenderer block={block} />;
            }

            case 'divider': {
                return (
                    <DividerBlock
                        content={block.content}
                        onUpdate={(id, data) => onBlockUpdate({ ...block, ...data })}
                        blockId={block.id}
                        isEditMode={isEditMode}
                    />
                );
            }

            case 'apa-quote': {
                return <ApaQuoteRenderer block={block} isEditMode={isEditMode} />;
            }

            case 'apa-citation': {
                return <ApaCitationRenderer block={block} />;
            }

            case 'apa-reference-list': {
                return <ApaReferenceListRenderer block={block} autoCitations={autoApaCitations} />;
            }

            case 'accordion': {
                return <AccordionRenderer block={block} />;
            }

            case 'project-timeline': {
                return (
                    <TimelineRenderer
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                    />
                );
            }

            case 'cards': {
                return (
                    <CardRenderer
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                    />
                );
            }

            case 'multi-column-cards': {
                return (
                    <MultiColumnCardRenderer
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                        onRequestImage={(columnIndex, cardIndex, imageData) =>
                            onPresetImageRequest?.({ block, sectionId, columnIndex, cardIndex, imageData })
                        }
                        onRequestVideo={(columnIndex, cardIndex, videoData) =>
                            onPresetVideoRequest?.({ block, sectionId, columnIndex, cardIndex, videoData })
                        }
                    />
                );
            }

            case 'stacked-cards': {
                const items = block.items || [];
                const includeSideText = block.includeSideText !== false;

                const handleStackedCardUpdate = (index, updates) => {
                    const nextItems = items.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, ...updates } : item
                    ));
                    onBlockUpdate({ ...block, items: nextItems });
                };

                const handleActivateCard = (event, index) => {
                    if (!isEditMode) return;
                    if (event.target.closest('button, a, input, textarea, select')) return;
                    setActiveStackedCardIndex(index);
                };

                return (
                    <div className="my-6 space-y-4">
                        {items.map((item, index) => {
                            const backgroundColor = item.color || '#0b57d0';
                            const textColor = getContrastingTextColor(backgroundColor);
                            const isActive = isEditMode && activeStackedCardIndex === index;

                            if (!includeSideText) {
                                return (
                                    <div
                                        key={`${block.id || 'stacked-card'}-${index}`}
                                        className="rounded-2xl px-6 py-4 shadow-sm"
                                        style={{ backgroundColor, color: textColor }}
                                        onClick={(event) => handleActivateCard(event, index)}
                                    >
                                        {isActive ? (
                                            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                                                <input
                                                    type="text"
                                                    value={item.title || ''}
                                                    onChange={(event) => handleStackedCardUpdate(index, { title: event.target.value })}
                                                    className="w-full rounded-lg border border-white/40 bg-white/90 px-3 py-2 text-sm font-semibold text-slate-800"
                                                    placeholder="Card title"
                                                />
                                                <RichTextEditor
                                                    content={item.content || ''}
                                                    onChange={(content) => handleStackedCardUpdate(index, { content })}
                                                    isPreviewMode={false}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                {item.title && <h4 className="text-lg font-semibold mb-1">{item.title}</h4>}
                                                {item.content && (
                                                    <SafeHtml
                                                        className="text-sm leading-relaxed card-content"
                                                        html={item.content}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <div key={`${block.id || 'stacked-card'}-${index}`} className="grid grid-cols-[minmax(0,80px)_1fr] gap-4 items-start">
                                    <div className="text-right">
                                        {isActive ? (
                                            <input
                                                type="text"
                                                value={item.sideText || ''}
                                                onChange={(event) => handleStackedCardUpdate(index, { sideText: event.target.value })}
                                                className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-slate-700"
                                                placeholder="Side text"
                                            />
                                        ) : item.sideText ? (
                                            <div className="text-orange-500 font-semibold text-xl leading-none whitespace-pre-line">
                                                {item.sideText}
                                            </div>
                                        ) : (
                                            <div className="h-8" />
                                        )}
                                    </div>
                                    <div
                                        className="rounded-2xl px-6 py-4 shadow-sm"
                                        style={{ backgroundColor, color: textColor }}
                                        onClick={(event) => handleActivateCard(event, index)}
                                    >
                                        {isActive ? (
                                            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                                                <input
                                                    type="text"
                                                    value={item.title || ''}
                                                    onChange={(event) => handleStackedCardUpdate(index, { title: event.target.value })}
                                                    className="w-full rounded-lg border border-white/40 bg-white/90 px-3 py-2 text-sm font-semibold text-slate-800"
                                                    placeholder="Card title"
                                                />
                                                <RichTextEditor
                                                    content={item.content || ''}
                                                    onChange={(content) => handleStackedCardUpdate(index, { content })}
                                                    isPreviewMode={false}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                {item.title && <h4 className="text-lg font-semibold mb-1">{item.title}</h4>}
                                                {item.content && (
                                                    <SafeHtml
                                                        className="text-sm leading-relaxed card-content"
                                                        html={item.content}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            }

            // NEW: worksheet block renders the dedicated component
            case 'worksheet':
                return (
                    <WorksheetBlock
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                        isStudentView={!isEditMode}
                        onImportJson={handleWorksheetJsonImport}
                        onPasteJson={handleWorksheetJsonPaste}
                    />
                );

            case 'embedded-html':
                return (
                    <EmbeddedHtmlRenderer
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                    />
                );

            case 'custom-html':
                return (
                    <CustomHtmlRenderer
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                    />
                );

            case 'image_drawover':
                return (
                    <ImageDrawoverRenderer
                        block={block}
                        isEditMode={isEditMode}
                        onBlockUpdate={onBlockUpdate}
                    />
                );

            default:
                return <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">Unknown content type: {block.type}</div>;
        }
    };

    return (
        <div className="relative">
            <div
                ref={blockRef}
                className={`relative z-0 group transition-all duration-200 ${isEditMode ? 'border-2 border-dashed border-gray-300 p-4 m-2 rounded-xl hover:border-slate-400 hover:bg-slate-50/50' : ''} ${isDragging ? 'opacity-50' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onMouseDown={handleBlockMouseDown}
                onDragEnd={handleDragEnd}
            >
                {isEditMode && (
                    <div
                        className={`absolute right-2 z-10 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200 ${
                            block.type === 'html' ? 'top-1/2 -translate-y-1/2 transform' : 'top-2'
                        }`}
                    >
                        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit" className="w-8 h-8 bg-slate-700 text-white rounded-lg flex items-center justify-center shadow-md hover:bg-slate-800 transition-colors">
                            <Edit3 size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete" className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center shadow-md hover:bg-red-600 transition-colors">
                            <X size={14} />
                        </button>
                        <div className="drag-handle w-8 h-8 bg-gray-400 text-white rounded-lg flex items-center justify-center shadow-md cursor-grab hover:bg-gray-500 active:cursor-grabbing transition-colors" title="Drag to reorder" draggable={true} onDragStart={handleDragStart}>
                            <GripVertical size={14} />
                        </div>
                        {!isFirst && (
                            <button onClick={(e) => { e.stopPropagation(); onMoveUp(); }} className="w-8 h-8 bg-gray-400 text-white rounded-lg flex items-center justify-center shadow-md hover:bg-gray-500 transition-colors" title="Move Up">
                                <ChevronUp size={16} />
                            </button>
                        )}
                        {!isLast && (
                            <button onClick={(e) => { e.stopPropagation(); onMoveDown(); }} className="w-8 h-8 bg-gray-400 text-white rounded-lg flex items-center justify-center shadow-md hover:bg-gray-500 transition-colors" title="Move Down">
                                <ChevronDown size={16} />
                            </button>
                        )}
                    </div>
                )}
                <div className="content-wrapper" style={{ pointerEvents: isEditMode && isDragging ? 'none' : 'auto' }}>
                    {renderContent()}
                </div>
            </div>

            {isEditMode && (
                <div className="my-2 flex justify-center">
                    <AddBlockMenu onAddContent={handleAddContent} label="Add Below" />
                </div>
            )}
        </div>
    );
};

export default ContentBlock;
